import { first, map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { FieldService } from './field.service';
import { Crop, Field, FieldGroup } from '../_models';
import { CropService } from './crop.service';
import { CropSubUnit } from '../_models/cropSubUnit';
import { environment } from '../../../environments/environment';
import { StaticMapProxyService } from './static-map-proxy.service';
import { XYearService } from './xYear.service';

@Injectable()
export class CropSubUnitService {
  // Services
  private http = inject(HttpClient);
  private cropService = inject(CropService);
  private fieldService = inject(FieldService);
  private staticMapsProxyService = inject(StaticMapProxyService);
  private xYearService = inject(XYearService);

  private apiBaseUrl = environment.apiBaseUrl;
  cropSubUnitList$: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  cropSubUnitListLoaded$ = new BehaviorSubject<boolean>(false);
  cropSubUnitListFilteredToActivityMap$: BehaviorSubject<Array<any>> = new BehaviorSubject<
    Array<any>
  >([]);
  yearSelected = new Date().getFullYear();
  constructor() {
    this.xYearService.selectedYear$.subscribe((year) => {
      this.yearSelected = year;
    });
  }

  getAllCropSubUnitByYear(year: number): Observable<CropSubUnit[]> {
    year = !year ? new Date().getFullYear() : year;
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/cropSubUnits?cropYear=' + year)
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              const cropSubUnitList: Array<CropSubUnit> = response;
              this.cropSubUnitList$.next(
                this.setGeoJsonArea(this.setDataIntoCropSubUnitByCropId(cropSubUnitList))
              );
              this.cropSubUnitListLoaded$.next(true);
              return response;
            }
          }
        })
      );
  }

  getCropSubUnit(id: number): Observable<CropSubUnit> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/cropSubUnits/' + id)
      .pipe(map((response) => response));
  }

  getCropSubUnits(cropId: number): Observable<CropSubUnit> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/crop/' + cropId + '/cropSubUnits')
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              const cropSubUnitList: Array<CropSubUnit> = response;
              this.cropSubUnitList$.next(
                this.setGeoJsonArea(this.setDataIntoCropSubUnitByCropId(cropSubUnitList))
              );
              return response;
            }
          }
        })
      );
  }

  getSubUnitsByField(fieldId: number): Observable<Array<CropSubUnit>> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/cropSubUnits?fieldId=' + fieldId)
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              const cropSubUnitList: Array<CropSubUnit> = response;
              return this.setGeoJsonArea(this.setDataIntoCropSubUnitByCropId(cropSubUnitList));
            }
          }
        })
      );
  }

  // createCropSubUnit(cropId, cropSubUnit): Observable<CropSubUnit> {
  //     return this.http.post(this.apiBaseUrl + 'api/private/v2/crop/' + cropId + '/cropSubUnits', cropSubUnit)
  //         .map(response => response);
  // }

  createCropSubUnit(cropSubUnit): Observable<CropSubUnit> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    cropSubUnit.forEach((i) => {
      i.companyId = companySelected ? companySelected.id : null;
      if (!i.polygon || i.polygon === null || i.polygon === undefined) {
        delete i.polygon;
      }
    });
    return this.http
      .post<CropSubUnit>(this.apiBaseUrl + 'api/private/v2/cropSubUnits/bulk/', cropSubUnit)
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.getAllCropSubUnitByYear(this.yearSelected).subscribe();
              return response;
            }
          }
        })
      );
  }

  createCropSubUnitSingle(cropSubUnit): Observable<CropSubUnit> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    cropSubUnit.companyId = companySelected ? companySelected.id : null;
    return this.http
      .post<CropSubUnit>(
        this.apiBaseUrl + 'api/private/v2/cropSubUnits/' + cropSubUnit.id,
        cropSubUnit
      )
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.getAllCropSubUnitByYear(this.yearSelected).subscribe();
              return response;
            }
          }
        })
      );
  }

  updateCropSubUnit(cropSubUnit): Observable<CropSubUnit> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    cropSubUnit.companyId = companySelected ? companySelected.id : null;
    return this.http
      .put<CropSubUnit>(
        this.apiBaseUrl + 'api/private/v2/cropSubUnits/' + cropSubUnit.id,
        cropSubUnit
      )
      .pipe(
        map((response) => {
          if (response) {
            if (response) {
              this.getAllCropSubUnitByYear(this.yearSelected).subscribe();
              return response;
            }
          }
        })
      );
  }

  deleteCropSubUnit(cropSubUnit: CropSubUnit): Observable<CropSubUnit> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    cropSubUnit.companyId = companySelected ? companySelected.id : null;
    return this.http.delete(this.apiBaseUrl + 'api/private/v2/cropSubUnits/' + cropSubUnit.id).pipe(
      map((response) => {
        if (response) {
          this.getAllCropSubUnitByYear(this.yearSelected).subscribe();
          return { id: cropSubUnit.id } as CropSubUnit;
        }
      })
    );
  }

  setGeoJsonArea = (cropSubUnitList: Array<CropSubUnit>) => {
    const completeList = [];
    const geoJsonTmp = this.getGeoJsonFeatureCollection();
    geoJsonTmp.features.push(this.getGeoJsonFeature());
    _.each(cropSubUnitList, (cropSub: any) => {
      geoJsonTmp.features[0].geometry.coordinates = cropSub.polygon.coordinates;
      cropSub.geoJsonArea = JSON.stringify(geoJsonTmp);
      cropSub.staticMapUrl = this.staticMapsProxyService.getStaticMapUrl(
        JSON.parse(cropSub.geoJsonArea).features
      );
      completeList.push(cropSub);
    });
    return completeList;
  };

  private getGeoJsonFeatureCollection() {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  private getGeoJsonFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[]],
      },
    };
  }

  getCropSubUnitById = (id: number): CropSubUnit => {
    let item: CropSubUnit;
    this.cropSubUnitList$
      .pipe(first((list: CropSubUnit[]) => list && list.length > 0))
      .subscribe((list: CropSubUnit[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  setDataIntoCropSubUnitByCropId = (cropSubUnitList: Array<CropSubUnit>): Array<CropSubUnit> => {
    cropSubUnitList.forEach((cropsubUnit) => {
      const cropObj: Crop | undefined = this.cropService.getCropById(cropsubUnit.crop.id);
      if (cropObj && cropObj.id) {
        // Crea una copia profonda in modo sicuro
        const deepCropObj: Crop = JSON.parse(JSON.stringify(cropObj));

        const fieldObj: Field | undefined = this.fieldService.getFieldById(deepCropObj.field.id);
        if (fieldObj && fieldObj.id) {
          const deepFieldObj: Field = JSON.parse(JSON.stringify(fieldObj));
          deepCropObj.field.id = deepFieldObj.id;
          deepCropObj.field.name = deepFieldObj.name;

          if (deepFieldObj.group && deepFieldObj.group.name && deepFieldObj.group.id) {
            deepCropObj.field.group = {
              id: deepFieldObj.group.id,
              name: deepFieldObj.group.name,
            } as FieldGroup;
          }
        }
        cropsubUnit.crop = deepCropObj;
      }
    });
    return cropSubUnitList;
  };
}
